import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CreditCard, Plus, Pencil, Trash2, X, History,
  ShoppingBag, IndianRupee, Receipt, RotateCcw,
} from 'lucide-react';
import DataGrid from '../components/DataGrid';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  name: '', bank: 'HDFC Bank', last4: '',
  credit_limit: '', billing_day: 15, due_day: 5,
  interest_rate: 36, opening_balance: 0, notes: '',
};

const STATUS_STYLE = {
  Active: 'bg-emerald-900/30 text-emerald-300 border-emerald-700',
  Closed: 'bg-zinc-800 text-zinc-300 border-zinc-700',
};

const TX_STYLE = {
  spend:   { icon: ShoppingBag, color: 'text-red-400',   bg: 'bg-red-900/30 border-red-700',     label: 'Spent' },
  payment: { icon: IndianRupee, color: 'text-green-400', bg: 'bg-green-900/30 border-green-700', label: 'Paid' },
  refund:  { icon: RotateCcw,   color: 'text-cyan-400',  bg: 'bg-cyan-900/30 border-cyan-700',   label: 'Refund' },
};

/**
 * Credit Cards — separate registry from loans/OD.
 * Each card has its own statement cycle and the bank reconciliation
 * matcher can auto-link CC bill payments to a card here.
 */
export default function CreditCards() {
  const [cards, setCards] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [editCard, setEditCard]   = useState(null);
  const [detailCard, setDetailCard] = useState(null);
  const [txFor, setTxFor]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/credit-cards/`, { withCredentials: true }),
        axios.get(`${API}/credit-cards/summary`, { withCredentials: true }),
      ]);
      setCards(a.data || []);
      setSummary(b.data || null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load credit cards'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 space-y-4" data-testid="credit-cards-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CreditCard className="text-violet-400" size={22} />
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">Credit Cards</h1>
            <p className="text-zinc-500 text-sm">Track CC spend · pay statements from bank · auto-link from Bank Reconciliation</p>
          </div>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-cc-btn"
          className="inline-flex items-center gap-2 bg-violet-500 hover:bg-violet-400 text-black px-4 py-2 rounded-lg text-sm font-semibold">
          <Plus size={14} /> Add Card
        </button>
      </div>

      {summary && summary.total_cards > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="cc-summary">
          <SumCard label="Cards" val={summary.total_cards} />
          <SumCard label="Total Limit" val={fmt(summary.total_credit_limit)} />
          <SumCard label="Outstanding" val={fmt(summary.total_balance)} hot={summary.utilization_pct > 50} />
          <SumCard label="Available" val={fmt(summary.total_available)} color="text-emerald-300"/>
          <SumCard label="This Month Spend" val={fmt(summary.total_month_spend)} color="text-amber-300"/>
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm py-8 text-center">Loading credit cards…</div>
      ) : cards.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center">
          <CreditCard className="mx-auto text-zinc-700 mb-3" size={40}/>
          <h3 className="text-zinc-300 text-sm font-semibold mb-1">No credit cards yet</h3>
          <p className="text-zinc-500 text-xs mb-4">Add your first card to track spends and reconcile bill payments from Bank Reconciliation.</p>
          <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1.5 bg-violet-500 text-black px-3 py-1.5 rounded text-xs font-semibold">
            <Plus size={11}/> Add Card
          </button>
        </div>
      ) : (
        <DataGrid
          testId="cc-grid"
          title="Credit Cards"
          icon={<CreditCard size={14} className="text-violet-400"/>}
          rows={cards}
          rowKey={(r) => r.id}
          exportName="credit-cards"
          emptyText="No credit cards"
          columns={[
            { key: 'name', label: 'Card', type: 'text',
              accessor: r => r.name,
              render: r => (
                <button onClick={() => setDetailCard(r)} data-testid={`cc-name-${r.id}`}
                  className="text-zinc-100 hover:text-violet-300 text-left">
                  <div className="font-medium">{r.name}</div>
                  <div className="text-[10px] text-zinc-500">{r.bank} {r.last4 && `· ****${r.last4}`}</div>
                </button>
              ) },
            { key: 'credit_limit', label: 'Limit', type: 'number', align: 'right',
              accessor: r => r.credit_limit,
              render: r => <span className="font-mono text-zinc-300 text-xs">{fmt(r.credit_limit)}</span> },
            { key: 'current_balance', label: 'Outstanding', type: 'number', align: 'right',
              accessor: r => r.current_balance,
              render: r => <span className={`font-mono text-xs font-bold ${r.current_balance > 0 ? 'text-red-300' : 'text-emerald-300'}`}>{fmt(r.current_balance)}</span> },
            { key: 'available_credit', label: 'Available', type: 'number', align: 'right',
              accessor: r => r.available_credit,
              render: r => <span className="font-mono text-emerald-300 text-xs">{fmt(r.available_credit)}</span> },
            { key: 'utilization_pct', label: 'Util %', type: 'number', align: 'right',
              accessor: r => r.utilization_pct,
              render: r => {
                const c = r.utilization_pct > 75 ? 'text-red-400' : r.utilization_pct > 30 ? 'text-amber-300' : 'text-zinc-300';
                return <span className={`font-mono text-xs ${c}`}>{r.utilization_pct}%</span>;
              } },
            { key: 'this_month_spend', label: 'This Month', type: 'number', align: 'right',
              accessor: r => r.this_month_spend,
              render: r => <span className="font-mono text-zinc-200 text-xs">{fmt(r.this_month_spend)}</span> },
            { key: 'billing_day', label: 'Bill / Due', type: 'text',
              accessor: r => `${r.billing_day} / ${r.due_day}`,
              render: r => <span className="text-zinc-400 text-xs">{r.billing_day} / {r.due_day}</span> },
            { key: 'status', label: 'Status', type: 'enum',
              accessor: r => r.status,
              render: r => <span className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded border ${STATUS_STYLE[r.status] || STATUS_STYLE.Active}`}>{r.status}</span> },
            { key: 'actions', label: '', type: 'text', sortable: false, filterable: false,
              accessor: r => r.id,
              render: r => (
                <div className="flex items-center gap-1 justify-end">
                  <button onClick={(e) => { e.stopPropagation(); setTxFor(r); }}
                    data-testid={`cc-tx-${r.id}`}
                    className="text-violet-400 hover:text-violet-300 text-xs inline-flex items-center gap-1">
                    <Plus size={11}/> Tx
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setEditCard(r); }}
                    data-testid={`cc-edit-${r.id}`}
                    className="text-zinc-400 hover:text-zinc-100 p-1">
                    <Pencil size={12}/>
                  </button>
                </div>
              ) },
          ]}
        />
      )}

      {showAdd && <CardModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }}/>}
      {editCard && <CardModal card={editCard} onClose={() => setEditCard(null)} onSaved={() => { setEditCard(null); load(); }}/>}
      {detailCard && <CardDetail card={detailCard} onClose={() => setDetailCard(null)} onChanged={() => load()}/>}
      {txFor && <TxModal card={txFor} onClose={() => setTxFor(null)} onSaved={() => { setTxFor(null); load(); }}/>}
    </div>
  );
}

function SumCard({ label, val, color = 'text-zinc-100', hot = false }) {
  return (
    <div className={`bg-zinc-900 border rounded-xl p-3 ${hot ? 'border-red-700' : 'border-zinc-800'}`}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wide">{label}</div>
      <div className={`font-mono text-base font-bold ${color}`}>{val}</div>
    </div>
  );
}

// ─── Add / Edit Card modal ───────────────────────────────────────────────
function CardModal({ card, onClose, onSaved }) {
  const [form, setForm] = useState(card ? { ...card } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const submit = async () => {
    if (!form.name) return toast.error('Card name required');
    setBusy(true);
    try {
      if (card) {
        await axios.put(`${API}/credit-cards/${card.id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/credit-cards/`, form, { withCredentials: true });
      }
      toast.success(card ? 'Card updated' : 'Card added');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };
  const del = async () => {
    if (!card || !window.confirm('Delete this card and all its transactions?')) return;
    try {
      await axios.delete(`${API}/credit-cards/${card.id}`, { withCredentials: true });
      toast.success('Card deleted');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="cc-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-zinc-100 text-base font-bold flex items-center gap-2">
            <CreditCard size={16} className="text-violet-400"/> {card ? 'Edit' : 'Add'} Credit Card
          </h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Field label="Card Name *" full><input value={form.name} onChange={e => set('name', e.target.value)} data-testid="cc-name-input" className="inp"/></Field>
          <Field label="Bank"><input value={form.bank} onChange={e => set('bank', e.target.value)} className="inp"/></Field>
          <Field label="Last 4 digits"><input value={form.last4} onChange={e => set('last4', e.target.value)} maxLength={4} className="inp"/></Field>
          <Field label="Credit Limit ₹"><input type="number" value={form.credit_limit} onChange={e => set('credit_limit', e.target.value)} data-testid="cc-limit-input" className="inp"/></Field>
          <Field label="APR % (interest)"><input type="number" step="any" value={form.interest_rate} onChange={e => set('interest_rate', e.target.value)} className="inp"/></Field>
          <Field label="Billing Day"><input type="number" min="1" max="31" value={form.billing_day} onChange={e => set('billing_day', e.target.value)} className="inp"/></Field>
          <Field label="Due Day"><input type="number" min="1" max="31" value={form.due_day} onChange={e => set('due_day', e.target.value)} className="inp"/></Field>
          {card && (
            <Field label="Status">
              <select value={form.status || 'Active'} onChange={e => set('status', e.target.value)} className="inp">
                <option>Active</option><option>Closed</option>
              </select>
            </Field>
          )}
          <Field label="Notes" full><textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="inp h-16" rows={2}/></Field>
        </div>
        <div className="flex items-center justify-between mt-4">
          {card && <button onClick={del} className="text-red-400 hover:text-red-300 text-xs inline-flex items-center gap-1"><Trash2 size={11}/> Delete</button>}
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-200 text-xs px-3 py-1.5">Cancel</button>
            <button onClick={submit} disabled={busy} data-testid="cc-save-btn"
              className="bg-violet-500 hover:bg-violet-400 disabled:opacity-40 text-black text-xs font-semibold px-4 py-1.5 rounded">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <style>{`.inp{width:100%;background:#0a0a0a;border:1px solid #27272a;border-radius:6px;padding:6px 8px;color:#f4f4f5}`}</style>
      </div>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <label className="text-zinc-400 mb-1 block text-[11px]">{label}</label>
      {children}
    </div>
  );
}

// ─── Card detail with transaction history ────────────────────────────────
function CardDetail({ card, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/credit-cards/${card.id}`, { withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error('Load failed'); }
  }, [card.id]);
  useEffect(() => { load(); }, [load]);

  const delTx = async (tx_id) => {
    if (!window.confirm('Delete this transaction?')) return;
    try {
      await axios.delete(`${API}/credit-cards/${card.id}/transactions/${tx_id}`, { withCredentials: true });
      toast.success('Transaction removed');
      load();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  if (!data) return null;
  const txs = data.transactions || [];
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="cc-detail">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-3xl p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-zinc-100 text-base font-bold">{card.name}</h2>
            <p className="text-zinc-500 text-xs">{card.bank} {card.last4 && `· ****${card.last4}`} · APR {card.interest_rate}%</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={16}/></button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4 text-xs">
          <SumCard label="Limit" val={fmt(card.credit_limit)}/>
          <SumCard label="Outstanding" val={fmt(data.current_balance)} hot={data.current_balance > 0}/>
          <SumCard label="Available" val={fmt(data.available_credit)} color="text-emerald-300"/>
          <SumCard label="This Month" val={fmt(data.this_month_spend)} color="text-amber-300"/>
        </div>

        <h3 className="text-zinc-300 text-sm font-semibold mb-2 flex items-center gap-2">
          <History size={14}/> Transactions ({txs.length})
        </h3>
        {txs.length === 0 ? (
          <div className="text-zinc-500 text-xs text-center py-6 bg-zinc-950 border border-zinc-800 rounded">No transactions yet.</div>
        ) : (
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {txs.slice().reverse().map(t => {
              const st = TX_STYLE[t.type] || TX_STYLE.spend;
              const Icon = st.icon;
              return (
                <div key={t.id} className={`flex items-center justify-between border rounded px-2 py-1.5 text-xs ${st.bg}`}>
                  <div className="flex items-center gap-2">
                    <Icon size={12} className={st.color}/>
                    <span className={st.color}>{st.label}</span>
                    <span className="text-zinc-300 font-mono">{fmt(t.amount)}</span>
                    {t.merchant && <span className="text-zinc-500">· {t.merchant}</span>}
                    {t.notes && <span className="text-zinc-500 italic">· {t.notes.slice(0, 40)}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-zinc-500 text-[10px]">{t.date}</span>
                    <button onClick={() => delTx(t.id)} className="text-red-400 hover:text-red-300" title="Delete">
                      <Trash2 size={10}/>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Record a transaction modal ─────────────────────────────────────────
function TxModal({ card, onClose, onSaved }) {
  const [form, setForm] = useState({
    type: 'spend', amount: '', date: today(), merchant: '', category: '', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const submit = async () => {
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error('Enter amount');
    setBusy(true);
    try {
      await axios.post(`${API}/credit-cards/${card.id}/transactions`, form, { withCredentials: true });
      toast.success('Transaction recorded');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="cc-tx-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-zinc-100 text-base font-bold">Record {card.name} Transaction</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={16}/></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <Field label="Type">
            <select value={form.type} onChange={e => set('type', e.target.value)} data-testid="cc-tx-type" className="inp">
              <option value="spend">Spend (merchant purchase)</option>
              <option value="payment">Payment (pay card bill)</option>
              <option value="refund">Refund</option>
            </select>
          </Field>
          <Field label="Amount ₹"><input type="number" value={form.amount} onChange={e => set('amount', e.target.value)} data-testid="cc-tx-amount" className="inp"/></Field>
          <Field label="Date"><input type="date" value={form.date} onChange={e => set('date', e.target.value)} className="inp"/></Field>
          <Field label="Merchant"><input value={form.merchant} onChange={e => set('merchant', e.target.value)} className="inp"/></Field>
          <Field label="Category" full><input value={form.category} onChange={e => set('category', e.target.value)} placeholder="Fuel · Travel · Office · …" className="inp"/></Field>
          <Field label="Notes" full><textarea value={form.notes} onChange={e => set('notes', e.target.value)} className="inp h-14" rows={2}/></Field>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-200 text-xs px-3 py-1.5">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="cc-tx-save"
            className="bg-violet-500 hover:bg-violet-400 disabled:opacity-40 text-black text-xs font-semibold px-4 py-1.5 rounded">
            {busy ? 'Saving…' : 'Record'}
          </button>
        </div>
        <style>{`.inp{width:100%;background:#0a0a0a;border:1px solid #27272a;border-radius:6px;padding:6px 8px;color:#f4f4f5}`}</style>
      </div>
    </div>
  );
}
